import { useState, useEffect } from 'react';
import datasetService from '../../../services/datasetService';
import IconButton from '/src/components/UI/Button/IconButton/IconButton';
import Button from '/src/components/UI/Button/Button';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import './StatementStatisticsGraph.css';
ChartJS.register(...registerables);
import TextInput from "/src/components/UI/TextField/TextInput";
import Loader from '/src/components/loader/Loader';

const StatementStatisticsGraph = ({ column, allColumns, datasetName }) => {
    const { getDataset } = datasetService;
    const [rows, setRows] = useState([]);
    const [chartType, setChartType] = useState('bar');
    const [activeFilters, setActiveFilters] = useState({});
    const [showFilters, setShowFilters] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!column) return;
        setActiveFilters({});

        const fetchAllRows = async () => {
            setLoading(true);
            let page = 1;
            let allRows = [];
            while (true) {
                const result = await getDataset(decodeURI(datasetName), { page });
                if (!result?.[0]?.data) break;
                const rows = result[0].data;
                allRows = [...allRows, ...rows];
                if (rows.length < result[0].per_page) break;
                page++;
            }
            setRows(allRows);
            setLoading(false);
        };

        fetchAllRows();
    }, [column]);

    if (loading) return <Loader />;


    if (!column) return null;

    const filteredRows = Object.keys(activeFilters).length > 0
        ? rows.filter(row => Object.entries(activeFilters).every(([colId, val]) => String(row[colId]) === String(val)))
        : rows;

    const counts = {};
    filteredRows.forEach(row => {
        const val = row[column.id];
        if (val == null || val === '') return;
        counts[String(val)] = (counts[String(val)] || 0) + 1;
    });

    const sortedEntries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const labels = sortedEntries.map(([k]) => k != null && k !== '' ? k : 'NULL');
    const values = sortedEntries.map(([, v]) => v);
    const bestValue = sortedEntries[0]?.[0] != null && sortedEntries[0]?.[0] !== '' ? sortedEntries[0][0] : 'NULL';
    const lowestValue = sortedEntries[sortedEntries.length - 1]?.[0] != null && sortedEntries[sortedEntries.length - 1]?.[0] !== '' ? sortedEntries[sortedEntries.length - 1][0] : 'NULL';
    const bestCount = sortedEntries[0]?.[1] ?? 0;
    const lowestCount = sortedEntries[sortedEntries.length - 1]?.[1] ?? 0;

    const numericValues = rows.map(r => parseFloat(r[column.id])).filter(v => !isNaN(v));
    const avg = numericValues.length ? (numericValues.reduce((a, b) => a + b, 0) / numericValues.length).toFixed(2) : null;
    const sorted = [...numericValues].sort((a, b) => a - b);
    const median = sorted.length ? sorted[Math.floor(sorted.length / 2)] : null;
    const q1 = sorted.length ? sorted[Math.floor(sorted.length * 0.25)] : null;
    const q2 = sorted.length ? sorted[Math.floor(sorted.length * 0.5)] : null;
    const q3 = sorted.length ? sorted[Math.floor(sorted.length * 0.75)] : null;
    const q4 = sorted.length ? sorted[sorted.length - 1] : null;

    const TOP_N = 4;
    const sortedAll = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const topEntries = sortedAll.slice(0, TOP_N);
    const otherSum = sortedAll.slice(TOP_N).reduce((acc, [, v]) => acc + v, 0);
    const pieLabels = [...topEntries.map(([k]) => k != null && k !== '' ? k : 'NULL'), ...(otherSum > 0 ? ['Other'] : [])];
    const pieValues = [...topEntries.map(([, v]) => v), ...(otherSum > 0 ? [otherSum] : [])];

    const pieData = {
        labels: pieLabels,
        datasets: [{ data: pieValues, backgroundColor: ['#36a2eb', '#ff6384', '#4caf50', '#ffeb3b', '#009688'], borderWidth: 2, borderColor: '#fff' }]
    };
    const pieOptions = { responsive: true, maintainAspectRatio: false, cutout: '50%', plugins: { legend: { position: 'top' } } };
    const chartData = {
        labels,
        datasets: [{ label: column.name, data: values, backgroundColor: ['#0E7AED', '#36a2eb', '#ff6384', '#ff9f40', '#4bc0c0', '#9966ff', '#ffcd56'] }]
    };

    return (
        <div className="ss-graph-section">
            <div className="ss-graph-box">
                <h3>{column.name}</h3>
                <p className="ss-subtitle">Type: {column.type || 'other'}</p>

                {Object.keys(counts).length <= 20 && (
                    <div className="ss-filters">
                        <div className="ss-filter-tags">
                            <span className="ss-filter-label">Filters</span>
                            {Object.entries(activeFilters).map(([colId, val]) => {
                                const col = allColumns.find(c => c.id === colId);
                                return (
                                    <span key={colId} className="ss-filter-tag">
                                        {col?.name}: {val}
                                        <span className="ss-filter-tag-remove"
                                            onClick={() => setActiveFilters(f => { const n = {...f}; delete n[colId]; return n; })}>✕</span>
                                    </span>
                                );
                            })}
                            <IconButton className="icons_up main-iconButton profil_action main_add-button space_tableButton" icon="fas fa-plus" title="Add filter" onClick={() => setShowFilters(!showFilters)} />
                        </div>
                        {showFilters && (
                            <div className="ss-filter-panel">
                                <div className="ss-filter-panel-body">
                                    <input type="text" style={{ display: 'none' }} />
                                    <input type="password" style={{ display: 'none' }} />
                                    <div className="ss-filter-grid">
                                    {allColumns.filter(col => col.id !== 'id').map(col => (
                                        <div key={col.id} className="ss-filter-row">
                                            <TextInput name={`filter_${col.id}`} placeholder={`Filter by ${col.name}`} value={activeFilters[col.id] ?? ''} onChange={e => { e.stopPropagation();setActiveFilters(f => ({ ...f, [col.id]: e.target.value })); }} onClick={e => e.stopPropagation()} style={{ marginBottom: '5px' }}/>
                                            {activeFilters[col.id] && (
                                                <span className="ss-filter-row-remove"
                                                    onClick={() => setActiveFilters(f => { const n = {...f}; delete n[col.id]; return n; })}>✕</span>
                                            )}
                                        </div>
                                    ))}
                                    </div>
                                </div>
                                <div className="ss-filter-panel-footer">
                                    <Button label="Apply" className="validate" onClick={() => setShowFilters(false)} />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="ss-stats-row">
                    <div className="ss-stat-box">
                        <div className="ss-stat-box-info">
                            <i className="fas fa-info-circle ss-info-icon"></i>
                            <div className="ss-info-tooltip">
                                <p><strong>Max value:</strong> The most frequently occurring value in this column.</p>
                                <p><strong>Min value:</strong> The least frequently occurring value in this column.</p>
                            </div>
                        </div>
                        <div className="ss-stat-grid">
                            <strong>Max value</strong><span className="ss-colon">:</span><span>{bestValue} ({bestCount})</span>
                            <strong>Min value</strong><span className="ss-colon">:</span><span>{lowestValue} ({lowestCount})</span>
                        </div>
                    </div>
                    {column.type === 'integer' && (
                        <div className="ss-stat-box">
                            <div className="ss-stat-box-info">
                                <i className="fas fa-info-circle ss-info-icon"></i>
                                <div className="ss-info-tooltip">
                                    <p><strong>Average:</strong> The arithmetic mean of all numeric values.</p>
                                    <p><strong>Median:</strong> The middle value when all values are sorted.</p>
                                </div>
                            </div>
                            <div className="ss-stat-grid">
                                <strong>Average</strong><span className="ss-colon">:</span><span>{avg}</span>
                                <strong>Median</strong><span className="ss-colon">:</span><span>{median}</span>
                            </div>
                        </div>
                    )}
                    {column.type === 'integer' && q1 !== null && (
                        <div className="ss-stat-box">
                            <div className="ss-stat-box-info">
                                <i className="fas fa-info-circle ss-info-icon"></i>
                                <div className="ss-info-tooltip">
                                    <p><strong>Q1:</strong> 25% of values fall below this point.</p>
                                    <p><strong>Q2:</strong> The median, 50% of values fall below.</p>
                                    <p><strong>Q3:</strong> 75% of values fall below this point.</p>
                                    <p><strong>Q4:</strong> The maximum value in the dataset.</p>
                                </div>
                            </div>
                            <div className="ss-stat-grid" style={{ fontSize: 13 }}>
                                <strong>Q1</strong><span className="ss-colon">:</span><span>{q1}</span>
                                <strong>Q2</strong><span className="ss-colon">:</span><span>{q2}</span>
                                <strong>Q3</strong><span className="ss-colon">:</span><span>{q3}</span>
                                <strong>Q4</strong><span className="ss-colon">:</span><span>{q4}</span>
                            </div>
                        </div>
                    )}
                </div>

                {Object.keys(counts).length > 20 ? (
                    <div className="ss-no-preview">No preview available</div>
                ) : (
                    <>
                        <div className="ss-chart-selector">
                            <IconButton icon="fas fa-chart-line" className="icons_up main-iconButton profil_action main_add-button space_tableButton" onClick={() => setChartType('bar')} style={chartType === 'bar' ? { background: 'var(--main)' , color: 'white'} : {}}/>
                            <IconButton icon="fas fa-chart-pie" className="icons_up main-iconButton profil_action main_add-button space_tableButton" onClick={() => setChartType('pie')} style={chartType === 'pie' ? { background: 'var(--main)' , color: 'white'} : {}}/>


                        </div>

                        <div className="ss-chart-container">
                            {chartType === 'bar'
                                ? <Chart type="bar" data={chartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { title: { display: true, text: column.name } }, y: { title: { display: true, text: 'Count' } } } }} />
                                : <Chart type="doughnut" data={pieData} options={pieOptions} />
                            }
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default StatementStatisticsGraph;
